import format from 'date-fns/format';
import parse from 'date-fns/parse';
import { isValid } from 'date-fns';

import { DEFAULT_DATE_FORMAT } from 'constants/common';

import { formatDate } from 'helpers/formatDate';
import { getTranslation } from 'helpers/getTranslation';

const API_DATE_FORMAT = 'yyyy-MM-dd';

export type CuratorReportApiRiskLevel = 'critical' | 'warning' | 'ok';
export type CuratorReportDisplayRiskLevel = 'warning' | 'ok' | 'stuck';
export type CuratorReportStudentStatus = 'active' | 'access_expired';

export type CuratorReportCurrentLessonContext = {
  id?: string;
  title: string;
  preview: string | null;
};

export type CuratorReportPromocodeAccess = {
  startDate: string | null;
  endDate: string | null;
} | null;

export type CuratorReportProgressBucket = {
  progress: number;
  count: number;
};

export type CuratorReportCourseProgressChart = {
  id: string;
  title: string;
  progressDistribution: CuratorReportProgressBucket[];
};

export type CuratorReportCurrentLesson = {
  id?: string;
  title: string;
  preview?: string | null;
  course: CuratorReportCurrentLessonContext;
  module: CuratorReportCurrentLessonContext | null;
};

export type CuratorReportStudent = {
  id: string;
  name: string | null;
  email: string;
  avatar: string | null;
  progressPercent: number;
  status: CuratorReportStudentStatus;
  currentLesson: CuratorReportCurrentLesson | null;
  daysInactive: number | null;
  apiRiskLevel: CuratorReportApiRiskLevel;
  riskScore: number;
  promocodeAccess: CuratorReportPromocodeAccess;
  primaryCourseId: string;
};

export type CuratorReportStallPoint = {
  lessonId: string;
  lessonTitle: string;
  averageRating: number;
  reportCount: number;
};

export type CuratorReportSummary = {
  total: number;
  active: number;
  onTrack: number;
  stuck: number;
  atRisk: number;
  completed: number;
  accessExpired: number;
  avgProgressPercent: number;
};

export type CuratorReportSchool = {
  id?: string;
  name: string;
  preview?: string | null;
};

export type CuratorReportCourse = {
  id: string;
  title: string;
};

export type CuratorReportPagination = {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
};

export type CuratorReportSortableColumn =
  | 'progress'
  | 'status'
  | 'daysInactive'
  | 'access';

export type CuratorReportSortParams = {
  sortBy: CuratorReportSortableColumn | '';
  sortDirection: 'asc' | 'desc';
};

export type CuratorReportData = {
  school: CuratorReportSchool;
  course: CuratorReportCourse;
  updatedAt: string;
  summary: CuratorReportSummary;
  students: CuratorReportStudent[];
  stallPoints: CuratorReportStallPoint[];
  courseProgressCharts: CuratorReportCourseProgressChart[];
  pagination?: CuratorReportPagination;
};

const RISK_BADGE_CLASS_MAP: Record<CuratorReportDisplayRiskLevel, string> = {
  warning: 'riskWarning',
  ok: 'riskOk',
  stuck: 'riskStuck',
};

const STATUS_BADGE_CLASS_MAP: Record<CuratorReportStudentStatus, string> = {
  active: 'statusOnTrack',
  access_expired: 'statusAccessExpired',
};

type ApiCuratorReportResponse = {
  school: { id?: string; name: string; preview?: string | null };
  course: { id: string; title: string };
  cohort: {
    total: number;
    active?: number;
    onTrack: number;
    stuck: number;
    atRisk: number;
    completed: number;
    notStarted?: number;
    accessExpired?: number;
    avgProgress: number;
  };
  students: Array<{
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    avatar?: string | null;
    progress: number;
    status: CuratorReportStudentStatus;
    currentLesson?: {
      id?: string;
      title: string;
      preview?: string | null;
      course: CuratorReportCurrentLessonContext;
      module: CuratorReportCurrentLessonContext | null;
    } | null;
    daysInactive: number;
    risk: {
      level: 'ok' | 'warning' | 'critical';
      score?: number;
    };
    promocodeAccess?: {
      startDate: string | null;
      endDate: string | null;
    } | null;
    primaryCourseId: string;
  }>;
  stallPoints: Array<{
    lessonId: string;
    title: string;
    averageRating: number;
    reportsCount: number;
  }>;
  courseProgressCharts?: Array<{
    id: string;
    title: string;
    progressDistribution: CuratorReportProgressBucket[];
  }>;
  updatedAt?: string;
  pagination?: CuratorReportPagination;
};

export const getDisplayRiskLevel = (
  apiRiskLevel: CuratorReportApiRiskLevel,
): CuratorReportDisplayRiskLevel => {
  if (apiRiskLevel === 'warning') {
    return 'stuck';
  }

  if (apiRiskLevel === 'critical') {
    return 'warning';
  }

  return 'ok';
};

const buildProgressDistributionFromStudents = (
  students: Array<{ progressPercent: number }>,
) => {
  const bucketSize = 10;
  const bucketCount = 100 / bucketSize + 1;
  const buckets = Array.from({ length: bucketCount }, (_, index) => ({
    progress: index * bucketSize,
    count: 0,
  }));
  const lastBucketIndex = buckets.length - 1;

  for (const student of students) {
    if (student.progressPercent >= 100) {
      buckets[lastBucketIndex].count += 1;
      continue;
    }

    const clamped = Math.max(0, Math.min(99, student.progressPercent));
    const bucketIndex = Math.floor(clamped / bucketSize);

    buckets[bucketIndex].count += 1;
  }

  return buckets;
};

export const mapCuratorReportResponse = (
  response: ApiCuratorReportResponse,
): CuratorReportData => ({
  school: {
    id: response.school.id,
    name: response.school.name,
    preview: response.school.preview ?? null,
  },
  course: response.course,
  updatedAt: response.updatedAt ?? new Date().toISOString(),
  summary: {
    total: response.cohort.total,
    active:
      response.cohort.active ??
      (response.cohort.onTrack ?? 0) +
        (response.cohort.stuck ?? 0) +
        (response.cohort.atRisk ?? 0),
    onTrack: response.cohort.onTrack,
    stuck: response.cohort.stuck,
    atRisk: response.cohort.atRisk,
    completed: response.cohort.completed,
    accessExpired: response.cohort.accessExpired ?? 0,
    avgProgressPercent: response.cohort.avgProgress,
  },
  students: response.students.map((student) => {
    const name = [student.firstName, student.lastName]
      .filter(Boolean)
      .join(' ');

    return {
      id: student.id,
      name: name || null,
      email: student.email,
      avatar: student.avatar ?? null,
      progressPercent: student.progress,
      status: student.status,
      currentLesson: student.currentLesson
        ? {
            id: student.currentLesson.id ?? '',
            title: student.currentLesson.title,
            preview: student.currentLesson.preview ?? null,
            course: student.currentLesson.course,
            module: student.currentLesson.module,
          }
        : null,
      daysInactive: student.daysInactive,
      apiRiskLevel: student.risk.level,
      riskScore: student.risk.score ?? 0,
      promocodeAccess: student.promocodeAccess
        ? {
            startDate: student.promocodeAccess.startDate ?? null,
            endDate: student.promocodeAccess.endDate ?? null,
          }
        : null,
      primaryCourseId: student.primaryCourseId,
    };
  }),
  stallPoints: response.stallPoints.map((point) => ({
    lessonId: point.lessonId,
    lessonTitle: point.title,
    averageRating: point.averageRating,
    reportCount: point.reportsCount,
  })),
  courseProgressCharts: response.courseProgressCharts?.length
    ? response.courseProgressCharts.map((chart) => ({
        id: chart.id,
        title: chart.title,
        progressDistribution: chart.progressDistribution.map((bucket) => ({
          progress: bucket.progress,
          count: bucket.count,
        })),
      }))
    : [
        {
          id: response.course.id,
          title: response.course.title,
          progressDistribution: buildProgressDistributionFromStudents(
            response.students.map((student) => ({
              progressPercent: student.progress,
            })),
          ),
        },
      ],
  pagination: response.pagination,
});

export const hasStudentName = (name: string | null | undefined) =>
  Boolean(name?.trim());

export const getStudentDisplayName = (name: string | null | undefined) =>
  hasStudentName(name)
    ? name!.trim()
    : getTranslation('school_curator_report_page.student_name_not_set');

export const getStudentAvatarText = (student: {
  name: string | null;
  email: string;
}) => student.name?.trim() || student.email || '';

export const isAllLessonsCompleted = (
  currentLesson: CuratorReportCurrentLesson | null,
  progressPercent: number,
) => !currentLesson && progressPercent >= 100;

export const isRiskApplicable = (
  status: CuratorReportStudentStatus,
  apiRiskLevel: CuratorReportApiRiskLevel,
) =>
  status === 'active' &&
  (apiRiskLevel === 'warning' || apiRiskLevel === 'critical');

export const getRiskNotApplicableLabel = (
  status: CuratorReportStudentStatus,
  apiRiskLevel: CuratorReportApiRiskLevel,
) => {
  if (status === 'active' && apiRiskLevel === 'ok') {
    return getTranslation('school_curator_report_page.risk.no_risk');
  }

  return getTranslation('school_curator_report_page.risk.not_applicable');
};

export const getRiskNotApplicableBadgeClassName = () => 'riskNotApplicable';

export const getRiskBadgeClassName = (
  apiRiskLevel: CuratorReportApiRiskLevel,
) =>
  RISK_BADGE_CLASS_MAP[getDisplayRiskLevel(apiRiskLevel)] ??
  RISK_BADGE_CLASS_MAP.ok;

export const getStatusBadgeClassName = (status: CuratorReportStudentStatus) =>
  STATUS_BADGE_CLASS_MAP[status] ?? STATUS_BADGE_CLASS_MAP.active;

export const getRiskLevelLabel = (apiRiskLevel: CuratorReportApiRiskLevel) =>
  getTranslation(
    `school_curator_report_page.risk.${getDisplayRiskLevel(apiRiskLevel)}`,
  );

export const getStudentStatusLabel = (status: CuratorReportStudentStatus) =>
  getTranslation(`school_curator_report_page.status.${status}`);

export const formatProgressPercent = (value: number) => `${Math.round(value)}%`;

export const formatRatingScore = (value: number) =>
  value.toFixed(1).replace('.', ',');

export const formatDaysInactive = (days: number | null) => {
  if (days === null || days === undefined) {
    return '—';
  }

  return getTranslation('school_curator_report_page.days_inactive_value', {
    count: days,
  });
};

export const getStallPointRatingColor = (averageRating: number) => {
  if (averageRating >= 4) {
    return '#85E0AB';
  }

  if (averageRating >= 3) {
    return '#FDCE82';
  }

  return '#F98A8A';
};

export type SummaryCardVariant =
  | 'default'
  | 'success'
  | 'stuck'
  | 'warning'
  | 'info'
  | 'muted';

export type SummaryCard = {
  key: string;
  label: string;
  value: number | string;
  variant: SummaryCardVariant;
  icon: string;
};

export const formatAccessFilterDateForApi = (
  displayDate: string,
): string | undefined => {
  const trimmedDate = displayDate.trim();

  if (!trimmedDate) {
    return undefined;
  }

  const parsedDate = parse(trimmedDate, DEFAULT_DATE_FORMAT, new Date());

  if (!isValid(parsedDate)) {
    return undefined;
  }

  return format(parsedDate, API_DATE_FORMAT);
};

export const hasActiveAccessFilter = (accessFrom: string, accessTo: string) =>
  Boolean(accessFrom.trim() || accessTo.trim());

export const formatStudentAccessUntil = (
  promocodeAccess: CuratorReportPromocodeAccess,
  locale: string,
) => {
  if (!promocodeAccess?.endDate) {
    return getTranslation('school_curator_report_page.not_available');
  }

  if (promocodeAccess.endDate === 'NEVER') {
    return getTranslation('school_curator_report_page.access.unlimited');
  }

  return getTranslation('school_curator_report_page.access.until', {
    date: formatDate(promocodeAccess.endDate, 'dd MMM yyyy', locale),
  });
};

export const getSummaryCards = (
  summary: CuratorReportSummary,
): SummaryCard[] => [
  {
    key: 'total',
    label: getTranslation('school_curator_report_page.summary.total'),
    value: summary.total,
    variant: 'default',
    icon: 'students',
  },
  {
    key: 'active',
    label: getTranslation('school_curator_report_page.summary.active'),
    value: summary.active ?? 0,
    variant: 'success',
    icon: 'checkRound',
  },
  {
    key: 'completed',
    label: getTranslation('school_curator_report_page.summary.completed'),
    value: summary.completed,
    variant: 'info',
    icon: 'published',
  },
  {
    key: 'accessExpired',
    label: getTranslation('school_curator_report_page.summary.access_expired'),
    value: summary.accessExpired ?? 0,
    variant: 'muted',
    icon: 'calendar',
  },
  {
    key: 'avgProgress',
    label: getTranslation('school_curator_report_page.summary.avg_progress'),
    value: formatProgressPercent(summary.avgProgressPercent),
    variant: 'info',
    icon: 'dashboard',
  },
  {
    key: 'atRisk',
    label: getTranslation('school_curator_report_page.summary.at_risk'),
    value: summary.atRisk,
    variant: 'warning',
    icon: 'warning',
  },
];
